import React from 'react';
import { motion } from 'framer-motion';
import {
  MessageCircle,
  User,
  Clock,
  CheckCircle,
  Phone,
  Video,
  Globe,
  ChevronRight,
} from 'lucide-react';

export function Counselling() {
  const features = [
    {
      icon: <Clock />,
      title: 'Convenient & Flexible',
      description: 'Schedule sessions that fit your life, not the other way around. Available 24/7.',
    },
    {
      icon: <User />,
      title: 'Expert Counsellors',
      description: 'Connect with experienced and compassionate Australian mental health professionals.',
    },
    {
      icon: <Globe />,
      title: 'Accessible Anywhere',
      description: 'Receive support from wherever you are, across Australia.',
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white p-6 md:p-8">
      {/* Hero Section */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-7xl mx-auto mb-12"
      >
        <div className="bg-gradient-to-r from-idecide-primary to-idecide-dark rounded-3xl p-8 md:p-12 text-white shadow-xl">
          <div className="flex items-center gap-4 mb-4">
            <MessageCircle className="text-idecide-primary-light" size={40} />
            <h1 className="text-3xl md:text-4xl font-bold">Anytime Counselling</h1>
          </div>
          <p className="text-lg text-idecide-primary-light/90 max-w-3xl mb-6">
            Your On-Demand Support: Access professional, confidential counselling sessions whenever you need them, 
            from the comfort of your home. Connect with qualified Australian counsellors via video or phone.
          </p>
          <ul className="space-y-3 text-base text-idecide-primary-light/90 mb-8">
            <li className="flex items-center gap-2"><CheckCircle size={20} /> Qualified Australian Counsellors</li>
            <li className="flex items-center gap-2"><CheckCircle size={20} /> Video & Phone Sessions</li>
            <li className="flex items-center gap-2"><CheckCircle size={20} /> Flexible Booking & Availability</li>
          </ul>
          <motion.a
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            href="https://anytimecounselling.com.au/"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white text-idecide-primary px-8 py-3 rounded-full text-base font-semibold shadow-lg hover:bg-gray-100 transition-colors flex items-center justify-center gap-2"
          >
            Book an Anytime Counselling Session <ChevronRight size={20} />
          </motion.a>
        </div>
      </motion.div>

      {/* Features Section */}
      <div className="max-w-7xl mx-auto mb-12">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">
          Why Choose Anytime Counselling?
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="h-full"
            >
              <div className="group h-full bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 flex flex-col items-center text-center">
                <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-6 transition-all duration-300 group-hover:bg-idecide-primary/20">
                  <div className="text-idecide-primary transition-transform duration-300 group-hover:scale-110">
                    {React.cloneElement(feature.icon, { size: 32 })}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">{feature.title}</h3>
                <p className="text-gray-600 flex-grow leading-relaxed">{feature.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Contact Section */}
      <div className="max-w-7xl mx-auto mb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-gradient-to-r from-idecide-accent to-idecide-primary rounded-3xl p-8 md:p-12 text-white shadow-xl flex flex-col md:flex-row items-center justify-between"
        >
          <div className="text-center md:text-left mb-6 md:mb-0">
            <h2 className="text-2xl md:text-3xl font-bold mb-2 text-idecide-primary-light">Ready to Start Your Journey?</h2>
            <p className="text-lg text-idecide-accent-light/90">Contact us today to learn more or book your first session.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <motion.a
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              href="tel:1800123456" // Replace with actual Anytime Counselling contact
              className="bg-white text-idecide-primary px-6 py-3 rounded-full text-base font-semibold shadow-md hover:bg-gray-100 transition-colors flex items-center gap-2 justify-center"
            >
              <Phone size={20} /> Call Us
            </motion.a>
            <motion.a
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              href="https://anytimecounselling.com.au/" // Placeholder, replace with actual site
              target="_blank"
              rel="noopener noreferrer"
              className="border border-white/80 text-white/80 px-6 py-3 rounded-full text-base font-semibold hover:bg-white/10 hover:border-white hover:text-white transition-colors flex items-center gap-2 justify-center"
            >
              <Video size={20} /> Visit Website
            </motion.a>
          </div>
        </motion.div>
      </div>
    </div>
  );
} 